import { useCurrentDate, Button } from "@sellar-io/components";
import { ReactNode, useState } from "react";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import {
  ContextOrderBasketType,
  ProductItem,
  useBasketContext,
  useEvents,
  useSellarUser,
} from "../../../hooks";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { getProductStockToDisplay } from "../helpers";
import { CheckoutSelectedFulfillmentType } from "../StockList";
import { useStockListContext } from "../useStockListContext";
import TagManager from "react-gtm-module";
import { Toast } from "../../../components/toast/Toast";
import { useToast } from "../../../components/toast/useToast.shared";
import { Icon } from "../../../components";
import {
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
  generateContainerTypeDeliveryChargeRulesText,
  isIneligibleForFulfillmentType,
  pluralize,
} from "@sellar-io/common";
import { useEditExistingOrderStore } from "./Storefront/EditExistingOrderDialog/EditExistingOrderStore";
import { useStorefrontPromotion } from "./Storefront/StorefrontPromotion/StorefrontPromotionProvider.shared";
import { isStorefrontAvailableAt } from "@sellar-io/common";
import { useIneligibleForCourierStore } from "../../../hooks/useIneligibleForCourierStore";
import { AlertCompact } from "../../../components/alert/AlertCompact";
import { ContainerTypeEligibilityContainerTypesType } from "@sellar-io/api-v2/types";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";

const MaxQuantityChip = ({ maxQuantity }: { maxQuantity: number | null }) => {
  if (maxQuantity) {
    return (
      <div className="justify-center rounded-sm border border-zinc-200 bg-zinc-50 px-4 py-2 text-xs font-medium text-zinc-800">
        <div className="flex flex-nowrap items-center gap-2">
          <Icon icon={"SPEEDOMETER"} size={0.8} />
          <div className="truncate text-nowrap">
            Max {maxQuantity} {pluralize("item", maxQuantity)}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const getQuantity = (
  order: ContextOrderBasketType | null,
  productItem: { id: number },
) => {
  if (!order) return 0;
  const orderItem = order.OrderItems?.find(
    (i) => i.Product.id == productItem.id,
  );
  return orderItem ? orderItem.quantity : 0;
};

export const ItemQuantity = ({
  productItem,
  showMaxQuantity,
  selectedFulfillmentType,
}: {
  productItem: ProductItem;
  showMaxQuantity?: boolean;
  selectedFulfillmentType?: CheckoutSelectedFulfillmentType;
}) => {
  const currentDate = useCurrentDate();
  const { toast } = useToast();
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const [searchParams] = useSearchParams();
  const { business } = useSellarCurrentBusiness();
  const editExistingOrderStore = useEditExistingOrderStore();
  const [showUpsWarningDialog, setShowUpsWarningDialog] =
    useState<boolean>(false);
  const [hasShownUpsWarningDialog, setHasShownUpsWarningDialog] =
    useState<boolean>(false);
  const [showCourierToHaulierSwitch, setShowCourierToHaulierSwitch] =
    useState<boolean>(false);
  const {
    supplierQuery,
    submittedOrder,
    openOrderList,
    courierFulfillmentType,
    supplierStorefront,
  } = useStockListContext();
  const {
    setHasItemIneligibleForCourier,
    hasSeenSwitchModal,
    setHasSeenSwitchModal,
  } = useIneligibleForCourierStore();
  const [{ temporaryCartId, modifyOrderItem, order }] = useBasketContext();
  const { applyEligiblePromotionRewards, claimedPromotionRewardItemIds } =
    useStorefrontPromotion();
  const { user } = useSellarUser();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const orderFlowToken = searchParams.get("orderFlowToken");

  const loggedInAndPriceTier =
    (!!order?.id || !!productItem.ProductPriceTier) && !!business && !!user;

  const isEditingExistingOpenOrder =
    !!order?.id && openOrderList.map((order) => order.id).includes(order.id);

  const productStockAvailable = getProductStockToDisplay(
    productItem,
    order,
    submittedOrder,
  );

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  const unbundlingEnabled =
    (supplierStorefront?.deliveryMethods?.length ?? 0) > 0;

  // storefront availability

  const storefrontAvailability = supplierQuery.data?.StorefrontAvailability;
  const isStorefrontAvailable = storefrontAvailability
    ? isStorefrontAvailableAt(currentDate, storefrontAvailability)
    : false;

  const currentQuantity = getQuantity(order, productItem);
  const hasReachedMaxOrderQuantity =
    typeof productItem.maxOrderQuantity === "number"
      ? currentQuantity >= productItem.maxOrderQuantity
      : false;
  const canAddQuantity =
    (loggedInAndPriceTier || storefrontLink || orderFlowToken) &&
    (productItem?.canBackorder ||
      (productStockAvailable > 0 && currentQuantity < productStockAvailable)) &&
    !hasReachedMaxOrderQuantity &&
    isStorefrontAvailable;

  const canSubtractQuantity =
    (loggedInAndPriceTier || storefrontLink || orderFlowToken) &&
    currentQuantity > 0 &&
    isStorefrontAvailable;

  const amountToShowInStockDropdown = (): number => {
    if (productItem.canBackorder) {
      return 500;
    }

    if (productStockAvailable > 0) {
      if (productStockAvailable > 200) {
        //suppliers have been setting availableStock to numbers like 10000, causes memory issues with the real DOM, crashes on storefronts
        return 201;
      }
      return productStockAvailable + 1;
    }

    return 1;
  };

  const isIneligibleContainer =
    !unbundlingEnabled &&
    isIneligibleForFulfillmentType({
      fulfillmentType: courierFulfillmentType,
      item: productItem,
    });

  const incrementItemQuantity = async () => {
    if (hasReachedMaxOrderQuantity) {
      const toastCopy = `Max quantity per customer: ${productItem.maxOrderQuantity}`;
      toast({
        placement: "bottom",
        render: () => <Toast className="flex">{toastCopy}</Toast>,
      });
    }

    if (canAddQuantity) {
      modifyOrderItem?.({
        productItem,
        quantity: currentQuantity + 1,
        applyEligiblePromotionRewards: ({ order }) => {
          return applyEligiblePromotionRewards({
            order,
            claimedPromotionRewardItemIds,
          });
        },
      });

      TagManager.dataLayer({
        dataLayer: {
          event: "product_added_to_basket",
        },
      });

      const isPreOrder = !!findLatestPreOrderAvailableFromDate([
        { availableFrom: productItem.availableFrom },
      ]);

      await track("Product added to cart", {
        temporaryCartId,
        context: "storefront",
        supplierId: supplierQuery.data?.id,
        supplierCount: 1,
        isConsolidatedBasket: false,
        productId: productItem.id,
        productName: productItem.name,
        productType: productItem.type,
        productStock: productItem.stock,
        supplier: supplierQuery.data?.urlId,
        isPreOrder,
        sessionId,
      });
    }
  };

  const itemIsLargeDraught =
    productItem.unit === "L" &&
    productItem.volume === 50 &&
    (productItem.containerType?.toLowerCase().includes("keg") ||
      productItem.containerType?.toLowerCase().includes("cask"));

  return (
    <div className="flex w-min flex-col gap-2">
      <div className={"flex justify-between gap-2"}>
        <div
          className={twMerge(
            "align-center flex h-10 w-10 cursor-pointer justify-center rounded-sm border border-solid border-gray-200 bg-gray-100 text-3xl",
            !canSubtractQuantity && "cursor-not-allowed opacity-25",
          )}
          onClick={async () => {
            if (canSubtractQuantity) {
              modifyOrderItem?.({
                productItem,
                quantity: currentQuantity - 1,
                applyEligiblePromotionRewards: ({ order }) => {
                  return applyEligiblePromotionRewards({
                    order,
                    claimedPromotionRewardItemIds,
                  });
                },
              });

              const isPreOrder = !!findLatestPreOrderAvailableFromDate([
                { availableFrom: productItem.availableFrom },
              ]);

              if (
                isIneligibleContainer &&
                currentQuantity === 1 &&
                palletOverrideEnabled
              ) {
                setHasItemIneligibleForCourier(false);
                setHasSeenSwitchModal(false);
              }

              if (
                itemIsLargeDraught &&
                currentQuantity === 1 &&
                !palletOverrideEnabled
              ) {
                setHasItemIneligibleForCourier(false);
              }

              await track("Product removed from cart", {
                temporaryCartId,
                context: "storefront",
                supplierId: supplierQuery.data?.id,
                supplierCount: 1,
                isConsolidatedBasket: false,
                productId: productItem.id,
                productName: productItem.name,
                productType: productItem.type,
                productStock: productItem.stock,
                supplier: supplierQuery.data?.urlId,
                isPreOrder,
                sessionId,
              });
            }
          }}
        >
          −
        </div>
        <select
          disabled={
            (productStockAvailable <= 0 && !productItem.canBackorder) ||
            (!loggedInAndPriceTier && !storefrontLink && !orderFlowToken) ||
            !isStorefrontAvailable
          }
          className={
            "flex h-10 w-10 appearance-none justify-center rounded-sm border border-solid bg-white text-center text-2xl"
          }
          style={{
            //for safari center align
            ["textAlign" as string]: "-webkit-center",
          }}
          value={!!productItem ? currentQuantity : 0}
          onChange={(e) => {
            const quantity = parseInt(e.target.value);
            if (isNaN(quantity)) {
              return;
            }
            modifyOrderItem?.({
              productItem,
              quantity,
              applyEligiblePromotionRewards: ({ order }) => {
                return applyEligiblePromotionRewards({
                  order,
                  claimedPromotionRewardItemIds,
                });
              },
            });
          }}
        >
          {[...Array(amountToShowInStockDropdown()).keys()].map((i) => (
            <option value={i} key={i} className={"p-2"}>
              {i}
            </option>
          ))}
        </select>
        <div
          className={twMerge(
            "align-center flex h-10 w-10 cursor-pointer justify-center rounded-sm border border-solid border-gray-200 bg-gray-100 text-3xl",
            !canAddQuantity && "cursor-not-allowed opacity-25",
          )}
          onClick={async () => {
            if (
              openOrderList.length &&
              !isEditingExistingOpenOrder &&
              !editExistingOrderStore.hasShownEditExistingOrderDialog
            ) {
              editExistingOrderStore.showEditExistingOrderDialog();
              return;
            }

            const isCourier =
              selectedFulfillmentType && selectedFulfillmentType === "courier";
            if (
              !unbundlingEnabled &&
              isCourier &&
              !hasSeenSwitchModal &&
              isIneligibleContainer &&
              palletOverrideEnabled
            ) {
              return setShowCourierToHaulierSwitch(true);
            }

            if (
              !unbundlingEnabled &&
              isCourier &&
              !hasShownUpsWarningDialog &&
              itemIsLargeDraught &&
              !palletOverrideEnabled
            ) {
              return setShowUpsWarningDialog(true);
            }

            if (!unbundlingEnabled && isIneligibleContainer) {
              setHasItemIneligibleForCourier(true);
            }

            incrementItemQuantity();
          }}
        >
          +
        </div>
      </div>
      {showMaxQuantity && (
        <MaxQuantityChip maxQuantity={productItem.maxOrderQuantity ?? null} />
      )}
      <UpsKegWarningDialog
        active={showUpsWarningDialog}
        onDismiss={() => {
          setShowUpsWarningDialog(false);
          setHasShownUpsWarningDialog(true);
        }}
      />
      <CourierToHaulierSwitchDialog
        ProductItem={productItem}
        active={showCourierToHaulierSwitch}
        onDismiss={() => setShowCourierToHaulierSwitch(false)}
        onAccept={() => {
          setShowCourierToHaulierSwitch(false);
          setHasItemIneligibleForCourier(true);
          setHasSeenSwitchModal(true);
          incrementItemQuantity();
        }}
      />
    </div>
  );
};

const UpsKegWarningDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        leftIcon={"CHEVRON_DOWN"}
        title="Notice about products sold in 50L containers"
        description={`Please note that some couriers are no longer accepting 50L containers. Please consider ordering products in smaller containers if you do not intend to have your order delivery on a pallet via haulier.`}
      />
      <DialogCTA>
        <Button onClick={onDismiss}>I understand</Button>
      </DialogCTA>
    </Dialog>
  );
};

const CourierToHaulierSwitchDialog = ({
  active,
  onDismiss,
  onAccept,
  ProductItem,
}: {
  active: boolean;
  onDismiss: () => void;
  onAccept: () => void;
  ProductItem: ProductItem;
}) => {
  const { courierFulfillmentType, haulierFulfillmentType } =
    useStockListContext();

  const getFreeDeliveryText = (): ReactNode => {
    let points = <></>;

    haulierFulfillmentType?.DeliveryChargeRules.forEach((rule) => {
      points = (
        <>
          {points}
          <DialogBullet>
            Get free delivery when you spend {formatCurrency(rule.value)} or
            more
          </DialogBullet>
        </>
      );
    });

    haulierFulfillmentType?.ContainerTypeDeliveryChargeRules.forEach((rule) => {
      points = (
        <>
          {points}
          <DialogBullet>
            {generateContainerTypeDeliveryChargeRulesText({
              value: rule.value,
              containerType: rule.containerType,
            })}
          </DialogBullet>
        </>
      );
    });

    return points;
  };

  const getContainerDisplayText = (
    containerType: ContainerTypeEligibilityContainerTypesType,
  ) => {
    switch (containerType) {
      case "cask":
        return "casks";
      case "plasticKeg":
        return "plastic kegs";
      case "steelKeg":
        return "steel kegs";
      default:
        return "";
    }
  };

  const getEligibilityText = () => {
    let message = "This supplier can only send small-pack";

    courierFulfillmentType?.ContainerTypeEligibilityRules.forEach((rule) => {
      message = `${message}, ${rule.volume}${rule.unit} ${getContainerDisplayText(rule.containerType)}`;
    });

    return `${message} via courier.`;
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        leftIcon={"CHEVRON_DOWN"}
        title="Switch to Pallet Delivery?"
      />
      <DialogContent className="flex flex-col gap-4">
        <AlertCompact type="warning" className="font-semibold">
          {ProductItem.name} can only be delivered on a pallet
        </AlertCompact>
        <div className="flex flex-col gap-4">
          <div className="flex w-full gap-4 rounded-lg border border-zinc-300 p-4">
            <figure className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-zinc-100">
              <Icon icon="PALLET" size={1.3} />
            </figure>
            <div className="flex flex-col">
              <span className="font-semibold">Pallet delivery</span>
              <ul className="pl-2">
                <DialogBullet>Great value on large orders</DialogBullet>
                <DialogBullet>Guaranteed to arrive together</DialogBullet>
                <DialogBullet>
                  Access to all formats including steel kegs and casks
                </DialogBullet>
                {getFreeDeliveryText()}
              </ul>
            </div>
          </div>
          <div className="flex w-full gap-4 rounded-lg border border-zinc-300 p-4">
            <figure className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-zinc-100">
              <Icon icon="DELIVERY_BOX" size={1.3} />
            </figure>
            <div className="flex flex-col">
              <span className="font-semibold">Courier</span>
              <ul className="pl-2">
                <DialogBullet>More economical for smaller orders</DialogBullet>
                <DialogBullet>{getEligibilityText()}</DialogBullet>
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="w-full">
        <div className="flex w-full flex-col-reverse md:w-auto md:flex-row">
          <Button
            color="transparent"
            onClick={onDismiss}
            className="w-full md:w-auto"
          >
            Keep courier delivery
          </Button>
          <Button onClick={onAccept} className="w-full md:w-auto">
            Switch to pallet
          </Button>
        </div>
      </DialogCTA>
    </Dialog>
  );
};

const DialogBullet = ({ children }: { children: ReactNode }) => {
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon icon="CIRCLE" size={0.3} className="mt-1.5 flex-none" />
      {children}
    </li>
  );
};
